import { getTradeBreakdowns } from "./tradeBreakdowns";
import { buildPerformanceTimeline, getNetTradeProfit, getPositionStats } from "./tradeStats";
import { PERFORMANCE_STAT_ROWS } from "./performanceStatsRows";

const WORKBOOK_MIME_TYPE = "application/vnd.ms-excel;charset=utf-8";

const getFirstValue = (...values) => values.find((value) => value !== null && value !== undefined && value !== "");

const getModifiedStopLoss = (trade = {}) => (
  getFirstValue(trade.modified_sl, trade.modified_stop_loss, trade.moved_stop_loss)
);

const TRADE_COLUMNS = [
  { key: "position_id", label: "Position ID", getValue: (trade) => getFirstValue(trade.position_id, trade.ticket) },
  { key: "magic_number", label: "Magic Number" },
  { key: "magic", label: "Strategy" },
  { key: "type", label: "Type" },
  { key: "order_type", label: "Order Type", getValue: (trade) => (trade.type === 0 ? "BUY" : trade.type === 1 ? "SELL" : "") },
  { key: "symbol", label: "Symbol" },
  { key: "take_profit", label: "Take Profit" },
  { key: "stop_loss", label: "Stop Loss" },
  { key: "modified_sl", label: "Modified Stop Loss", getValue: getModifiedStopLoss },
  { key: "open_price", label: "Open Price", getValue: (trade) => getFirstValue(trade.open_price, trade.price) },
  { key: "close_price", label: "Close Price" },
  { key: "profit", label: "Profit Before Costs" },
  { key: "commission", label: "Commission" },
  { key: "swap", label: "Swap" },
  { key: "net_profit", label: "Net Profit", getValue: (trade) => getNetTradeProfit(trade) },
  { key: "pips", label: "Pips" },
  { key: "risk_reward_ratio", label: "Risk Reward Ratio" },
  { key: "volume", label: "Volume" },
  { key: "time_open", label: "Open Time", getValue: (trade) => trade.time_open || trade.open_time || trade.time },
  { key: "close_time", label: "Close Time", getValue: (trade) => trade.close_time || trade.time_close },
  { key: "is_open", label: "Is Open" },
  { key: "duration", label: "Duration" },
  { key: "created_at", label: "Created At" },
];

const escapeXml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;");

const isNumericValue = (value) => (
  value !== ""
  && value !== null
  && value !== undefined
  && typeof value !== "boolean"
  && Number.isFinite(Number(value))
);

const formatCellValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "TRUE" : "FALSE";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return value;
};

const buildCell = (value) => {
  const formattedValue = formatCellValue(value);
  const type = isNumericValue(formattedValue) ? "Number" : "String";

  return `<Cell><Data ss:Type="${type}">${escapeXml(formattedValue)}</Data></Cell>`;
};

const buildRow = (cells) => `<Row>${cells.map(buildCell).join("")}</Row>`;

const buildSectionTitle = (title, columnCount) => (
  `<Row><Cell ss:MergeAcross="${Math.max(columnCount - 1, 0)}"><Data ss:Type="String">${escapeXml(title)}</Data></Cell></Row>`
);

const buildWorksheet = (name, rows) => (
  `<Worksheet ss:Name="${escapeXml(name)}"><Table>${rows.join("")}</Table></Worksheet>`
);

const buildMetricRows = (metrics) => metrics.map(({ label, value }) => buildRow([label, value]));

const getTradeColumns = (positions = []) => {
  const knownKeys = new Set(TRADE_COLUMNS.map((column) => column.key));
  const excludedExtraKeys = new Set(["risk_amount_gbp"]);
  const extraColumns = Array.from(
    positions.reduce((keys, trade) => {
      Object.keys(trade || {}).forEach((key) => {
        if (!knownKeys.has(key) && !excludedExtraKeys.has(key) && key !== "ticket" && key !== "price" && key !== "open_time" && key !== "time" && key !== "time_close") {
          keys.add(key);
        }
      });
      return keys;
    }, new Set()),
  ).sort().map((key) => ({ key, label: key.replace(/_/g, " ") }));

  return [...TRADE_COLUMNS, ...extraColumns];
};

export const buildPastPerformanceWorkbook = ({
  positions = [],
  accountInfo = {},
  startingAmount = 0,
  cashflows = [],
  startDate = "",
  endDate = "",
  visibleStats = {},
} = {}) => {
  const stats = getPositionStats(positions, accountInfo, startingAmount, cashflows);
  const breakdowns = getTradeBreakdowns(positions, startingAmount, cashflows);
  const timeline = buildPerformanceTimeline(positions, cashflows, startingAmount);
  const endingAmount = timeline.endingBalance;
  const netCashFlow = cashflows.reduce((total, cashflow) => total + (Number(cashflow.amount) || 0), 0);

  const overviewRows = [
    buildSectionTitle("Summary", 2),
    ...buildMetricRows([
      { label: "Start Date", value: startDate },
      { label: "End Date", value: endDate },
      { label: "Starting Amount", value: startingAmount },
      { label: "Net Cash Flow", value: Number(netCashFlow.toFixed(2)) },
      { label: "Ending Amount", value: Number(endingAmount.toFixed(2)) },
      { label: "Total Trades", value: stats.total_trades },
      { label: "Net Profit", value: stats.total_profit },
      { label: "Gain %", value: stats.percentage_gain },
      { label: "Max Drawdown", value: stats.max_drawdown },
      { label: "Max Drawdown %", value: stats.max_drawdown_percent },
      { label: "Win Ratio %", value: stats.win_ratio },
    ]),
    buildRow([""]),
    buildSectionTitle("Monthly Net P/L / Drawdown", 3),
    buildRow(["Month", "Net P/L", "Drawdown"]),
    ...breakdowns.monthlyLabels.map((label, index) => buildRow([
      label,
      breakdowns.monthlyProfitValues[index],
      breakdowns.monthlyDrawdownValues[index],
    ])),
    buildRow([""]),
    buildSectionTitle("Trades By Pair", 3),
    buildRow(["Pair", "Trades", "Volume"]),
    ...breakdowns.pairTradeLabels.map((label, index) => buildRow([
      label,
      breakdowns.pairTradeCountValues[index],
      breakdowns.pairTradeVolumeValues[index],
    ])),
    buildRow([""]),
    buildSectionTitle("Net Profit By Strategy", 3),
    buildRow(["Strategy", "Net Profit", "Win Ratio %"]),
    ...breakdowns.strategyLabels.map((label, index) => buildRow([
      label,
      breakdowns.strategyProfitValues[index],
      breakdowns.strategyWinRatioValues[index],
    ])),
    buildRow([""]),
    buildSectionTitle("Net Profit By Pair", 3),
    buildRow(["Pair", "Net Profit", "Win Ratio %"]),
    ...breakdowns.symbolLabels.map((label, index) => buildRow([
      label,
      breakdowns.symbolProfitValues[index],
      breakdowns.symbolWinRatioValues[index],
    ])),
  ];

  const statsRows = [
    buildRow(["Metric", "Value"]),
    ...PERFORMANCE_STAT_ROWS
      .filter((stat) => visibleStats[stat.key] !== false)
      .map((stat) => buildRow([stat.label, stat.getValue(stats)])),
  ];

  const tradeColumns = getTradeColumns(positions);
  const tradeRows = [
    buildRow(tradeColumns.map((column) => column.label)),
    ...positions.map((trade) => buildRow(
      tradeColumns.map((column) => (
        column.getValue ? column.getValue(trade) : trade?.[column.key]
      )),
    )),
  ];

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:x="urn:schemas-microsoft-com:office:excel"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  ${buildWorksheet("Overview", overviewRows)}
  ${buildWorksheet("Stats", statsRows)}
  ${buildWorksheet("Trades", tradeRows)}
</Workbook>`;
};

export const downloadPastPerformanceWorkbook = (exportData) => {
  const workbook = buildPastPerformanceWorkbook(exportData);
  const blob = new Blob([workbook], { type: WORKBOOK_MIME_TYPE });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const startPart = exportData.startDate || "all";
  const endPart = exportData.endDate || "today";

  link.href = url;
  link.download = `past-performance-${startPart}-to-${endPart}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
